import { useRef, type ReactNode } from 'react'
import { useLocale } from '../../locale/LocaleContext.js'
import { Button } from '../ui/Button.js'

export interface EditorChooserProps {
  onPickGpx: (file: File) => void
  onDraw: () => void
}

export function EditorChooser({ onPickGpx, onDraw }: EditorChooserProps) {
  const { t } = useLocale()
  const fileRef = useRef<HTMLInputElement | null>(null)

  return (
    <section
      className="mx-auto flex w-full max-w-3xl flex-col gap-5 p-1"
      data-testid="editor-chooser"
    >
      <header className="flex flex-col gap-1 rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] p-4 shadow-sm">
        <p className="text-[11px] font-medium uppercase tracking-wide text-[var(--xr-text-muted)]">
          {t('editor.create.eyebrow')}
        </p>
        <h1 className="text-lg font-semibold text-[var(--xr-text-primary)]">
          {t('editor.chooser.title')}
        </h1>
        <p className="text-xs text-[var(--xr-text-muted)]">{t('editor.chooser.subtitle')}</p>
      </header>

      <input
        ref={fileRef}
        type="file"
        accept=".gpx,application/gpx+xml,application/xml,text/xml"
        className="hidden"
        data-testid="editor-chooser-gpx-input"
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) onPickGpx(file)
          event.target.value = ''
        }}
      />

      <div
        aria-label={t('editor.create.modeNav')}
        className="grid gap-3 sm:grid-cols-2"
        data-testid="editor-chooser-options"
        role="group"
      >
        <ChoiceCard
          title={t('editor.chooser.gpxTitle')}
          description={t('editor.chooser.gpxDesc')}
          badge={
            <span className="inline-flex items-center rounded-[var(--xr-radius-full)] bg-[var(--xr-brand-primary-soft)] px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[var(--xr-brand-primary)]">
              {t('editor.chooser.gpxBadge')}
            </span>
          }
          action={
            <Button onClick={() => fileRef.current?.click()} data-testid="editor-chooser-gpx">
              {t('editor.chooser.gpxCta')}
            </Button>
          }
          testId="editor-chooser-gpx-card"
        />
        <ChoiceCard
          title={t('editor.chooser.drawTitle')}
          description={t('editor.chooser.drawDesc')}
          badge={
            <span className="inline-flex items-center rounded-[var(--xr-radius-full)] bg-[var(--xr-bg-surface-elev)] px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[var(--xr-text-muted)]">
              {t('editor.chooser.drawBadge')}
            </span>
          }
          action={
            <Button variant="secondary" onClick={onDraw} data-testid="editor-chooser-draw">
              {t('editor.chooser.drawCtaHiking')}
            </Button>
          }
          testId="editor-chooser-draw-card"
        />
      </div>
    </section>
  )
}

function ChoiceCard({
  title,
  description,
  badge,
  action,
  testId,
}: {
  title: string
  description: string
  badge: ReactNode
  action: ReactNode
  testId: string
}) {
  return (
    <article
      className="flex min-h-44 flex-col justify-between gap-4 rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] p-4 shadow-sm"
      data-testid={testId}
    >
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold text-[var(--xr-text-primary)]">{title}</h3>
          {badge}
        </div>
        <p className="text-xs leading-relaxed text-[var(--xr-text-muted)]">{description}</p>
      </div>
      <div className="flex justify-end">{action}</div>
    </article>
  )
}
